import { Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError } from "@/auth";
import { ActionEditor } from "@/components/steps/ActionEditor";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const blankAction = (channel = "sms") => ({ channel, template_id: "", sms_text: "", delay_days: 0, delay_hours: 0 });
const numeric = (a) => ({ ...a, template_id: a.template_id || null, delay_days: Number(a.delay_days) || 0, delay_hours: Number(a.delay_hours) || 0 });

const StepPicker = ({ steps, value, onChange, anyLabel, testid }) => {
  const pipelines = [...new Set(steps.map((s) => s.pipeline))];
  const current = steps.find((s) => s.id === value);
  const [pipeline, setPipeline] = useState(current?.pipeline || "");
  return (
    <div className="sr-picker">
      <select value={pipeline} onChange={(e) => { setPipeline(e.target.value); onChange(""); }} data-testid={`${testid}-pipeline`}>
        <option value="">{anyLabel ? "Tous les pipelines" : "1. Choisir le pipeline"}</option>
        {pipelines.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={!pipeline} data-testid={testid}>
        <option value="">{anyLabel || "2. Choisir l'étape"}</option>
        {steps.filter((s) => s.pipeline === pipeline).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </div>
  );
};

export function RuleEditor({ rule, steps, templates, variables, onClose, onSaved, onNewTemplate }) {
  const [form, setForm] = useState({ name: rule?.name || "", from_step_id: rule?.from_step_id || "", to_step_id: rule?.to_step_id || "", is_active: rule?.is_active || false });
  const [actions, setActions] = useState([
    { channel: rule?.channel || "email", template_id: rule?.template_id || "", sms_text: rule?.sms_text || "", delay_days: rule?.delay_days || 0, delay_hours: rule?.delay_hours || 0 },
    ...(rule?.extra_actions || []).map((a) => ({ ...blankAction(), ...a, template_id: a.template_id || "" })),
  ]);
  const save = async () => {
    const [main, ...extra] = actions.map(numeric);
    const body = { ...form, ...main, from_step_id: form.from_step_id || null, extra_actions: extra };
    try {
      const { data } = rule ? await api.put(`/step-rules/${rule.id}`, body) : await api.post("/step-rules", body);
      if (data.warning) toast.warning(data.warning); else toast.success("Automatisation enregistrée.");
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="pj-dialog" data-testid="rule-editor">
        <DialogTitle className="pj-dialog-title">{rule ? "Modifier l'automatisation" : "Nouvelle automatisation"}</DialogTitle>
        <label>Nom<input placeholder="Ex. : SMS de bienvenue après qualification" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-name" /></label>
        <div className="sr-flow">
          <label>Quand un lead quitte…<StepPicker steps={steps} value={form.from_step_id} onChange={(v) => setForm({ ...form, from_step_id: v })} anyLabel="N'importe quelle étape" testid="rule-from" /></label>
          <span className="sr-arrow">→</span>
          <label>…et arrive en<StepPicker steps={steps} value={form.to_step_id} onChange={(v) => setForm({ ...form, to_step_id: v })} testid="rule-to" /></label>
        </div>
        <small className="pj-sub">Chaque envoi a son propre délai (0 = immédiat). Avec un délai, l'envoi est annulé si le lead a quitté l'étape entre-temps.</small>
        {actions.map((a, i) => (
          <ActionEditor key={i} action={a} index={i} templates={templates} variables={variables} onNewTemplate={onNewTemplate}
            onChange={(next) => setActions(actions.map((x, k) => (k === i ? next : x)))} onRemove={i > 0 ? () => setActions(actions.filter((_, k) => k !== i)) : null} />
        ))}
        {actions.length < 6 && (
          <button type="button" className="secondary-button pj-self-start" onClick={() => setActions([...actions, blankAction(actions[actions.length - 1].channel === "email" ? "sms" : "email")])} data-testid="rule-add-action">
            <Plus size={15} /> Ajouter un envoi (e-mail ou SMS)
          </button>
        )}
        <label className="sr-toggle"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="rule-active" />Activer cette automatisation</label>
        <button type="button" className="primary-button pj-self-start" onClick={save} disabled={!form.name || !form.to_step_id} data-testid="rule-save">Enregistrer</button>
      </DialogContent>
    </Dialog>
  );
}
